import { 
  startOfMonth, 
  endOfMonth, 
  addMonths, 
  subMonths, 
  startOfDay,
  endOfDay,
  set,
  addDays,
  isWeekend
} from 'date-fns';

/**
 * Retorna o próximo dia útil se a data cair em final de semana.
 */
function getNextBusinessDay(date: Date): Date {
  let d = date;
  while (isWeekend(d)) {
    d = addDays(d, 1);
  }
  return d;
}

/**
 * Calcula o intervalo de datas de um período de produção.
 * 
 * Regra: Se o dia de início é 20, o período de faturamento de "Abril/2026" 
 * inicia em 20/03/2026 e encerra em 20/04/2026 (inclusive).
 * Se o dia cair em final de semana, o início/fim é prorrogado para o próximo dia útil.
 * 
 * @param month Mês (0-11)
 * @param year Ano
 * @param startDay Dia de início da produção (1-31)
 */
export function getProductionPeriod(month: number, year: number, startDay: number = 1) {
  const referenceDate = new Date(year, month, 1);
  
  if (startDay === 1) {
    return {
      startDate: startOfDay(startOfMonth(referenceDate)),
      endDate: endOfDay(endOfMonth(referenceDate))
    };
  }

  // Se o dia de início for 20, o período de Abril (mês 3) 
  // começa em 20 de Março (mês 2) e termina em 20 de Abril (mês 3)
  const rawStartDate = set(subMonths(referenceDate, 1), { date: startDay });
  const rawEndDate = set(referenceDate, { date: startDay });

  const startDate = startOfDay(getNextBusinessDay(rawStartDate));
  const endDate = endOfDay(getNextBusinessDay(rawEndDate));

  return { startDate, endDate };
}

/**
 * Retorna o mês/ano (mês 0-11) do período de produção vigente na data de hoje.
 * Ex.: com startDay 21, em 28/09 o período vigente é "Outubro".
 */
export function getCurrentProductionMonth(startDay: number = 1): { month: number; year: number } {
  const now = new Date();

  if (startDay > 1 && now.getDate() >= startDay) {
    // Estamos no período que termina no mês seguinte
    const nextMonthDate = addMonths(now, 1);
    return { month: nextMonthDate.getMonth(), year: nextMonthDate.getFullYear() };
  }

  // Estamos no período que termina neste mês (ou startDay=1, que é o mês calendário)
  return { month: now.getMonth(), year: now.getFullYear() };
}

/**
 * Retorna o período de produção atual baseado na data de hoje.
 */
export function getCurrentProductionPeriod(startDay: number = 1) {
  const { month, year } = getCurrentProductionMonth(startDay);
  return getProductionPeriod(month, year, startDay);
}

/**
 * Retorna o nome formatado do período (Ex: "Abril/2026")
 */
export function formatProductionPeriodName(month: number, year: number) {
  const date = new Date(year, month, 1);
  return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

/**
 * Retorna uma lista dos últimos N períodos de produção.
 */
export function getRecentProductionPeriods(count: number = 12, startDay: number = 1) {
  const periods = [];
  const current = getCurrentProductionPeriod(startDay);
  
  // Usamos a endDate como referência para descobrir o mês/ano do período
  let refDate = current.endDate;

  for (let i = 0; i < count; i++) {
    const month = refDate.getMonth();
    const year = refDate.getFullYear();
    
    periods.push({
      month,
      year,
      label: formatProductionPeriodName(month, year),
      ...getProductionPeriod(month, year, startDay)
    });

    refDate = subMonths(refDate, 1);
  }

  return periods;
}
